/**
 * Applicant detail view with timeline and messaging
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Alert, Badge, Button, Card, EmptyState } from '../components/ui.jsx';
import {
  STAGE_LABELS, STAGE_COLORS, SOURCE_LABELS, SOURCE_COLORS,
  formatPhone, formatDateTime, formatDate, formatRelative
} from '../utils/format';
import { sanitizeProviderError } from '../utils/providerErrorSanitizer';
import { getApplicantProductivityFlags } from '../utils/applicantProductivity';
import { APPLICANT_DETAIL_REFRESH_MS, shouldRefreshApplicantDetail } from '../utils/applicantRefresh';
import { applyOptimisticStage, rollbackOptimisticStage } from '../utils/optimisticStageUpdate';
import {
  buildWorkflowRailItems,
  getNextActionButtons,
  getWorkflowToneClasses,
  summarizeCampaign,
} from '../utils/applicantWorkflowUi';

const ALL_STAGES = [
  'NEW', 'CONTACTED', 'SCREENING', 'INTERVIEW_SCHEDULED',
  'INTERVIEWED', 'HIRED', 'ONBOARDING', 'REJECTED', 'NO_SHOW', 'UNRESPONSIVE'
];

export default function ApplicantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showMoveStage, setShowMoveStage] = useState(false);
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showEditApplicant, setShowEditApplicant] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [retryingMessageId, setRetryingMessageId] = useState(null);
  const [newStage, setNewStage] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewNotes, setInterviewNotes] = useState('');
  const [noteText, setNoteText] = useState('');
  const [templates, setTemplates] = useState([]);
  const [toast, setToast] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const messagesEndRef = useRef(null);
  const smsComposerRef = useRef(null);
  const shortcutContactBlocked = Boolean(getApplicantProductivityFlags(data?.applicant || {}).contactBlockedLabel);

  const showToast = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  };

  const focusSmsComposer = useCallback(() => {
    smsComposerRef.current?.focus();
    smsComposerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);
  const fetchApplicant = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) setLoading(true);
      const appData = await api.get(`/applicants/${id}`);
      setData(appData);
      setLastRefreshedAt(new Date());
      setError('');
    } catch (err) {
      if (showLoading) {
        setError(err.message);
      } else {
        console.warn('Background applicant refresh failed', err);
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id]);

  const fetchTemplates = useCallback(async () => {
    try {
      const tmplData = await api.get('/settings/templates');
      setTemplates(tmplData.templates || []);
    } catch (err) {
      console.warn('Template refresh failed', err);
    }
  }, []);

  const fetchData = useCallback(async ({ showLoading = true, includeTemplates = false } = {}) => {
    await Promise.all([
      fetchApplicant({ showLoading }),
      includeTemplates ? fetchTemplates() : Promise.resolve()
    ]);
  }, [fetchApplicant, fetchTemplates]);

  useEffect(() => {
    fetchData({ includeTemplates: true });
    const intervalId = window.setInterval(() => {
      if (shouldRefreshApplicantDetail(document)) {
        fetchApplicant({ showLoading: false });
      }
    }, APPLICANT_DETAIL_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [fetchApplicant, fetchData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowMoveStage(false);
        setShowScheduleInterview(false);
        setShowAddNote(false);
        setShowEditApplicant(false);
        return;
      }

      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isTyping = ['input', 'textarea', 'select'].includes(activeTag);
      if (!shortcutContactBlocked && !isTyping && event.key.toLowerCase() === 't') {
        event.preventDefault();
        focusSmsComposer();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusSmsComposer, shortcutContactBlocked]);

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12 text-gray-500">Loading...</div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error || 'Applicant not found'}</div>
        </div>
      </Layout>
    );
  }

  const { applicant, timeline, stageHistory, interviews, dripQueue, workflow } = data;
  const safeResumeUrl = getSafeExternalUrl(applicant.resume_url);
  const productivityFlags = getApplicantProductivityFlags(applicant);
  const { stageAgeDays, isStale: isStaleApplicant, contactBlockedLabel } = productivityFlags;
  const isContactBlocked = Boolean(contactBlockedLabel);

  const handleSendSMS = async () => {
    if (!smsBody.trim() || isContactBlocked) return;
    setSmsSending(true);
    try {
      await api.post(`/applicants/${id}/messages`, { body: smsBody });
      setSmsBody('');
      await fetchData();
      showToast('Text sent');
    } catch (err) {
      const safeSendError = sanitizeProviderError(err.message) || 'Message failed to send. Please try again.';
      alert(`Message failed to send: ${safeSendError}`);
    } finally {
      setSmsSending(false);
    }
  };

  const handleRetryMessage = async (messageId) => {
    if (!messageId || retryingMessageId) return;
    if (isContactBlocked) return;
    setRetryingMessageId(messageId);
    try {
      await api.post(`/applicants/${id}/messages/${messageId}/retry`, {});
      await fetchData();
      showToast('Retry queued');
    } catch (err) {
      const safeRetryError = sanitizeProviderError(err.message) || 'Retry failed. Please try again.';
      alert(`Retry failed: ${safeRetryError}`);
    } finally {
      setRetryingMessageId(null);
    }
  };

  const handleMoveStage = async () => {
    if (!newStage) return;
    const previousData = data;
    const targetStage = newStage;
    setData(current => applyOptimisticStage(current, {
      stage: targetStage,
      changedBy: user?.name || 'Current user',
    }));
    setShowMoveStage(false);
    setNewStage('');
    showToast(`Moving to ${STAGE_LABELS[targetStage]}...`);

    try {
      await api.patch(`/applicants/${id}/stage`, { stage: targetStage });
      await fetchData({ showLoading: false });
      showToast(`Moved to ${STAGE_LABELS[targetStage]}`);
    } catch (err) {
      setData(current => rollbackOptimisticStage({ previous: previousData, current }));
      setShowMoveStage(true);
      setNewStage(targetStage);
      showToast('Stage move failed; reverted');
      alert(err.message);
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewDate) return;
    try {
      await api.post(`/applicants/${id}/interview`, {
        scheduled_at: interviewDate,
        notes: interviewNotes
      });
      setShowScheduleInterview(false);
      setInterviewDate('');
      setInterviewNotes('');
      await fetchData();
      showToast('Interview scheduled');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      const newNotes = applicant.notes
        ? `${applicant.notes}\n[${new Date().toLocaleDateString()}] ${noteText}`
        : `[${new Date().toLocaleDateString()}] ${noteText}`;
      await api.patch(`/applicants/${id}`, { notes: newNotes });
      setShowAddNote(false);
      setNoteText('');
      await fetchData();
      showToast('Note added');
    } catch (err) {
      alert(err.message);
    }
  };

  const openEditModal = () => {
    setEditForm({
      first_name: applicant.first_name || '',
      last_name: applicant.last_name || '',
      phone: applicant.phone || '',
      email: applicant.email || '',
      resume_url: applicant.resume_url || '',
      notes: applicant.notes || '',
    });
    setShowEditApplicant(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.patch(`/applicants/${id}`, editForm);
      setShowEditApplicant(false);
      await fetchData();
      showToast('Applicant updated');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateInterview = async (interviewId, status) => {
    try {
      await api.patch(`/interviews/${interviewId}`, { status });
      await fetchData();
      showToast('Interview updated');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReject = async () => {
    if (!confirm('Move this applicant to REJECTED?')) return;
    try {
      await api.patch(`/applicants/${id}/stage`, { stage: 'REJECTED' });
      await fetchData();
      showToast('Applicant rejected');
    } catch (err) {
      alert(err.message);
    }
  };

  const failedMessage = [...timeline]
    .filter(item => item.type === 'message' && item.direction === 'outbound' && item.status === 'failed')
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
  const actionableInterview = [...interviews]
    .filter(interview => interview.status === 'scheduled')
    .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at))[0];

  const handleWorkflowIntent = (intent) => {
    switch (intent) {
      case 'focus_sms':
        focusSmsComposer();
        break;
      case 'open_schedule_modal':
        setShowScheduleInterview(true);
        break;
      case 'mark_completed':
        if (actionableInterview?.id) handleUpdateInterview(actionableInterview.id, 'completed');
        break;
      case 'mark_no_show':
        if (actionableInterview?.id) handleUpdateInterview(actionableInterview.id, 'no_show');
        break;
      case 'retry_failed_text':
        if (failedMessage?.id) handleRetryMessage(failedMessage.id);
        break;
      case 'edit_applicant':
        openEditModal();
        break;
      case 'view_queue':
        document.querySelector('[data-timeline-card]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      default:
        break;
    }
  };

  const workflowRailItems = buildWorkflowRailItems(workflow, { formatTimestamp: formatDateTime });
  const nextActionButtons = getNextActionButtons(workflow?.nextAction, { contactBlocked: isContactBlocked });
  const campaignSummary = summarizeCampaign(workflow?.campaign);

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between no-print">
          <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/')} className="self-start">
            ← Back to applicants
          </Button>
          {lastRefreshedAt && (
            <p className="text-xs font-medium text-text-muted" aria-live="polite">
              Auto-refresh active · last checked {lastRefreshedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </p>
          )}
        </div>

        <Card padding="spacious" className="mb-5 overflow-hidden border-info-border bg-gradient-to-br from-white via-white to-info-bg/50 print:shadow-none">
          <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-blueSoft text-lg font-bold text-brand-blue ring-1 ring-info-border">
                  {getInitials(`${applicant.first_name || ''} ${applicant.last_name || ''}`)}
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
                      {applicant.first_name} {applicant.last_name}
                    </h1>
                    <button
                      onClick={openEditModal}
                      className="touch-target rounded-app px-2 text-sm font-semibold text-text-muted hover:bg-surface-hover hover:text-text-primary focus-ring no-print"
                      title="Edit applicant"
                      aria-label="Edit applicant"
                    >
                      Edit
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${STAGE_COLORS[applicant.stage]}`}>
                      {STAGE_LABELS[applicant.stage]}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${SOURCE_COLORS[applicant.source] || 'bg-slate-100 text-slate-700'}`}>
                      {SOURCE_LABELS[applicant.source] || applicant.source}
                    </span>
                    {contactBlockedLabel && <Badge variant="danger">{contactBlockedLabel}</Badge>}
                    {isStaleApplicant && <Badge variant="warning">{stageAgeDays}d in stage</Badge>}
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <InfoTile label="Phone" value={formatPhone(applicant.phone)} href={applicant.phone ? `tel:${applicant.phone}` : null} />
                {applicant.email && <InfoTile label="Email" value={applicant.email} href={`mailto:${applicant.email}`} />}
                <InfoTile label="Applied" value={formatDate(applicant.created_at)} />
                <InfoTile label="Assigned" value={applicant.assigned_name || 'Unassigned'} />
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-sm">
                {applicant.has_license !== null && (
                  <Badge variant={applicant.has_license ? 'success' : 'danger'}>
                    {applicant.has_license ? '✓ NY License' : '✗ No NY License'}
                  </Badge>
                )}
                {applicant.has_cdl_license !== null && applicant.has_cdl_license !== undefined && (
                  <Badge variant={applicant.has_cdl_license ? 'success' : 'neutral'}>
                    {applicant.has_cdl_license ? '✓ CDL License' : 'No CDL License'}
                  </Badge>
                )}
                {applicant.available_start && <Badge variant="neutral">Start: {applicant.available_start}</Badge>}
                <Badge variant={applicant.consent_status === 'opted_out' ? 'danger' : applicant.consent_status === 'opted_in' ? 'success' : 'neutral'}>
                  SMS: {applicant.consent_status.replace('_', ' ')}
                </Badge>
              </div>

              <div className="mt-4 space-y-2">
                {isContactBlocked && (
                  <Alert variant="danger" title={`${contactBlockedLabel}`}>
                    Texting is disabled for this applicant.
                  </Alert>
                )}
                {isStaleApplicant && (
                  <Alert variant="warning" title="Stale applicant">
                    {stageAgeDays} days in {STAGE_LABELS[applicant.stage]}.
                  </Alert>
                )}
                {applicant.notes && (
                  <div className="rounded-xl border border-border-default bg-surface-subtle px-3.5 py-3 text-sm text-text-secondary">
                    <p className="font-semibold text-text-primary">Internal notes</p>
                    <div className="mt-1 whitespace-pre-wrap text-[13px] leading-5">{applicant.notes}</div>
                  </div>
                )}
                {applicant.experience && (
                  <Alert variant="info" title="Experience">
                    {applicant.experience}
                  </Alert>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-border-default bg-white p-4 shadow-card no-print">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-text-muted">Next actions</p>
              <div className="mt-3 grid gap-2">
                <Button type="button" onClick={focusSmsComposer} disabled={isContactBlocked} className="w-full justify-start">💬 Send text</Button>
                <Button type="button" variant="secondary" onClick={() => setShowMoveStage(true)} className="w-full justify-start">Move stage</Button>
                <Button type="button" variant="secondary" onClick={() => setShowScheduleInterview(true)} className="w-full justify-start">📅 Schedule interview</Button>
                <Button type="button" variant="secondary" onClick={() => setShowAddNote(true)} className="w-full justify-start">📝 Add note</Button>
                <Button type="button" variant="danger" onClick={handleReject} className="w-full justify-start">Reject applicant</Button>
              </div>
            </div>
          </div>
        </Card>

        <div className="mb-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.65fr)] no-print">
          <WorkflowRailCard items={workflowRailItems} campaign={campaignSummary} workflow={workflow} />
          <NextActionCard
            workflow={workflow}
            buttons={nextActionButtons}
            contactBlocked={isContactBlocked}
            hasActionableInterview={Boolean(actionableInterview?.id)}
            hasFailedMessage={Boolean(failedMessage?.id)}
            onAction={handleWorkflowIntent}
          />
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.65fr)] print:grid-cols-1">
          <Card padding="compact" data-timeline-card className="flex min-h-[460px] flex-col overflow-hidden print:shadow-none">
            <div className="border-b border-border-default px-1 pb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue">Timeline</p>
              <h2 className="mt-1 text-lg font-bold text-text-primary">Conversation & activity</h2>
              <p className="text-sm text-text-muted">Messages, stage changes, interviews, and notes in one place.</p>
            </div>

            <div className="flex-1 overflow-y-auto px-1 py-4">
              {timeline.length === 0 ? (
                <EmptyState title="No activity yet" description="Send a text, schedule an interview, or move the applicant to start the timeline." icon="↯" />
              ) : (
                <div className="space-y-3">
                  {timeline.map((item, idx) => (
                    <TimelineItem
                      key={`${item.type}-${item.id || idx}`}
                      item={item}
                      onRetry={handleRetryMessage}
                      isRetrying={retryingMessageId === item.id}
                      retryDisabled={isContactBlocked}
                      retryDisabledReason={isContactBlocked ? `Cannot retry - ${contactBlockedLabel.toLowerCase()}` : null}
                    />
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="border-t border-border-default bg-surface-subtle p-4 no-print">
              <label htmlFor="sms-composer" className="mb-2 block text-sm font-semibold text-text-primary">Text message</label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <textarea
                  id="sms-composer"
                  ref={smsComposerRef}
                  value={smsBody}
                  onChange={e => setSmsBody(e.target.value)}
                  placeholder={isContactBlocked ? `Cannot send SMS - ${contactBlockedLabel.toLowerCase()}` : 'Type a message...'}
                  disabled={isContactBlocked}
                  rows={3}
                  aria-label="SMS message body"
                  className="min-h-[88px] flex-1 rounded-app border border-border-default bg-white px-3 py-2 text-sm text-text-primary shadow-sm focus-ring disabled:bg-slate-50 disabled:text-text-muted"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendSMS();
                    }
                  }}
                />
                <Button type="button" onClick={handleSendSMS} disabled={!smsBody.trim() || smsSending || isContactBlocked} loading={smsSending}>
                  Send
                </Button>
              </div>

              {templates.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {templates.slice(0, 5).map(t => (
                    <Button
                      key={t.template_key}
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={isContactBlocked}
                      title={isContactBlocked ? `Cannot use templates - ${contactBlockedLabel.toLowerCase()}` : undefined}
                      onClick={() => setSmsBody(
                        t.body
                          .replace(/\[first_name\]/g, applicant.first_name)
                          .replace(/\[last_name\]/g, applicant.last_name)
                      )}
                    >
                      {t.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </Card>

          <div className="space-y-5">
            {interviews.filter(i => i.status === 'scheduled').length > 0 && (
              <Card padding="normal">
                <PanelHeading eyebrow="Interviews" title="Upcoming" />
                <div className="mt-3 space-y-2">
                  {interviews.filter(i => i.status === 'scheduled').map(interview => (
                    <div key={interview.id} className="rounded-xl border border-info-border bg-info-bg p-3">
                      <p className="text-sm font-semibold text-info-text">{formatDateTime(interview.scheduled_at)}</p>
                      {interview.notes && <p className="mt-1 text-xs text-info-text/80">{interview.notes}</p>}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button type="button" size="sm" variant="success" onClick={() => handleUpdateInterview(interview.id, 'completed')}>Completed</Button>
                        <Button type="button" size="sm" variant="danger" onClick={() => handleUpdateInterview(interview.id, 'no_show')}>No show</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card padding="normal">
              <PanelHeading eyebrow="Workflow" title="Stage history" />
              {stageHistory.length === 0 ? (
                <p className="mt-3 text-sm text-text-muted">No stage history.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {stageHistory.map((s, idx) => (
                    <div key={idx} className="flex gap-3 rounded-xl border border-border-default bg-white p-3 text-sm">
                      <span className="mt-1 h-2 w-2 rounded-full bg-brand-blue" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${STAGE_COLORS[s.to_stage]}`}>
                            {STAGE_LABELS[s.to_stage]}
                          </span>
                          {s.optimistic && <span className="text-xs font-semibold text-brand-blue">saving…</span>}
                        </div>
                        <p className="mt-1 text-xs text-text-muted">
                          {formatRelative(s.created_at)}{s.changed_by ? ` · by ${s.changed_by}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {dripQueue.length > 0 && (
              <Card padding="normal">
                <PanelHeading eyebrow="Automation" title="Drip queue" />
                <div className="mt-3 divide-y divide-border-default">
                  {dripQueue.map(d => (
                    <div key={d.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <span className="font-medium text-text-primary">{d.template_key}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-text-muted">{formatDate(d.send_at)}</span>
                        <Badge variant={d.status === 'pending' ? 'warning' : d.status === 'sent' ? 'success' : 'neutral'}>{d.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {(safeResumeUrl || applicant.retell_recording_url) && (
              <Card padding="normal">
                <PanelHeading eyebrow="Files" title="Applicant assets" />
                <div className="mt-3 flex flex-wrap gap-2">
                  {safeResumeUrl && (
                    <Button as="a" href={safeResumeUrl} target="_blank" rel="noopener noreferrer" variant="secondary" size="sm">📄 Resume</Button>
                  )}
                  {applicant.retell_recording_url && (
                    <Button as="a" href={applicant.retell_recording_url} target="_blank" rel="noreferrer" variant="secondary" size="sm">🎙️ Recording</Button>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border-default bg-white/95 px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2 shadow-lg backdrop-blur sm:hidden no-print" aria-label="Common applicant actions">
        <div className="mx-auto grid max-w-md grid-cols-3 gap-2">
          <Button type="button" size="sm" onClick={focusSmsComposer} disabled={isContactBlocked}>Text</Button>
          <Button as="a" href={`tel:${applicant.phone}`} variant="secondary" size="sm">Call</Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => setShowMoveStage(true)}>Stage</Button>
        </div>
      </div>

      {toast && <Toast message={toast} />}

      {/* Move Stage Modal */}
      {showMoveStage && (
        <Modal title="Move Stage" onClose={() => setShowMoveStage(false)}>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Current stage: <strong>{STAGE_LABELS[applicant.stage]}</strong></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ALL_STAGES.filter(s => s !== applicant.stage).map(s => (
                <button
                  key={s}
                  onClick={() => setNewStage(s)}
                  className={`touch-target text-left px-3 py-2 rounded-lg text-sm border transition-colors focus-ring ${
                    newStage === s
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {STAGE_LABELS[s]}
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleMoveStage}
                disabled={!newStage}
                className="touch-target flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500 text-white py-2 rounded-lg text-sm font-medium focus-ring"
              >
                Move
              </button>
              <button
                onClick={() => setShowMoveStage(false)}
                className="touch-target flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 focus-ring"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Schedule Interview Modal */}
      {showScheduleInterview && (
        <Modal title="Schedule Interview" onClose={() => setShowScheduleInterview(false)}>
          <div className="space-y-3">
            <div>
              <label htmlFor="interview-date" className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
              <input
                id="interview-date"
                type="datetime-local"
                value={interviewDate}
                onChange={e => setInterviewDate(e.target.value)}
                className="input text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Use the browser calendar/time picker, then confirm with Schedule.</p>
            </div>
            <div>
              <label htmlFor="interview-notes" className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                id="interview-notes"
                value={interviewNotes}
                onChange={e => setInterviewNotes(e.target.value)}
                rows={2}
                className="input text-sm resize-none"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleScheduleInterview}
                disabled={!interviewDate}
                className="touch-target flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500 text-white py-2 rounded-lg text-sm font-medium focus-ring"
              >
                Schedule
              </button>
              <button
                onClick={() => setShowScheduleInterview(false)}
                className="touch-target flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 focus-ring"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Note Modal */}
      {showAddNote && (
        <Modal title="Add Note" onClose={() => setShowAddNote(false)}>
          <div className="space-y-3">
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Enter note..."
              rows={4}
              className="input text-sm resize-none"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleAddNote}
                disabled={!noteText.trim()}
                className="touch-target flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500 text-white py-2 rounded-lg text-sm font-medium focus-ring"
              >
                Add Note
              </button>
              <button
                onClick={() => setShowAddNote(false)}
                className="touch-target flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 focus-ring"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* Edit Applicant Modal */}
      {showEditApplicant && (
        <Modal title="Edit Applicant" onClose={() => setShowEditApplicant(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="edit-first-name" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  id="edit-first-name"
                  type="text"
                  value={editForm.first_name}
                  onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                  className="input text-sm"
                />
              </div>
              <div>
                <label htmlFor="edit-last-name" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  id="edit-last-name"
                  type="text"
                  value={editForm.last_name}
                  onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                  className="input text-sm"
                />
              </div>
            </div>
            <div>
              <label htmlFor="edit-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                id="edit-phone"
                type="tel"
                value={editForm.phone}
                onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                className="input text-sm"
              />
            </div>
            <div>
              <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                className="input text-sm"
              />
            </div>
            <div>
              <label htmlFor="edit-resume-url" className="block text-sm font-medium text-gray-700 mb-1">Resume URL</label>
              <input
                id="edit-resume-url"
                type="url"
                value={editForm.resume_url}
                onChange={e => setEditForm(f => ({ ...f, resume_url: e.target.value }))}
                placeholder="https://example.com/resume.pdf"
                className="input text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">HTTP and HTTPS links only.</p>
            </div>
            <div>
              <label htmlFor="edit-notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                id="edit-notes"
                value={editForm.notes}
                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                rows={4}
                className="input text-sm resize-none"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSaveEdit}
                className="touch-target flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium focus-ring"
              >
                Save
              </button>
              <button
                onClick={() => setShowEditApplicant(false)}
                className="touch-target flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 focus-ring"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
}

function WorkflowRailCard({ items, campaign, workflow }) {
  if (!workflow) {
    return (
      <Card padding="normal" className="border-warning-border bg-warning-bg">
        <h2 className="text-lg font-bold text-warning-text">Workflow</h2>
        <p className="mt-1 text-sm text-warning-text/80">Workflow status is unavailable. Refresh or try again.</p>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card padding="normal" className="border-warning-border bg-warning-bg">
        <h2 className="text-lg font-bold text-warning-text">Workflow</h2>
        <p className="mt-1 text-sm text-warning-text/80">Workflow status is unavailable. Refresh or try again.</p>
      </Card>
    );
  }

  return (
    <Card padding="normal" className="overflow-hidden bg-white">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue">Workflow</p>
          <h2 className="mt-1 text-lg font-bold text-text-primary">Where this applicant is in the hiring process.</h2>
        </div>
        <CampaignBadge campaign={campaign} />
      </div>

      <ol className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <li key={item.key} className={`rounded-2xl border p-3 ${item.classes.card}`}>
            <div className="flex gap-3">
              <span
                aria-label={item.iconLabel}
                className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-bold ring-4 ${item.classes.dot}`}
              >
                {item.icon}
              </span>
              <div className="min-w-0">
                <h3 className={`text-sm font-bold ${item.classes.label}`}>{item.label}</h3>
                <p className={`mt-1 text-xs leading-5 ${item.classes.helper}`}>{item.meta || 'No workflow timestamp yet.'}</p>
              </div>
            </div>
          </li>
        ))}
      </ol>

      {workflow.needsAttention?.active && workflow.needsAttention.reasons?.length > 0 && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-bold text-amber-900">Needs attention</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {workflow.needsAttention.reasons.map((reason) => (
              <Badge key={reason.key} variant={reason.severity === 'urgent' ? 'danger' : reason.severity === 'warning' ? 'warning' : 'neutral'} title={reason.helper}>
                {reason.label}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function CampaignBadge({ campaign }) {
  if (!campaign) return null;
  const variant = campaign.status === 'failed'
    ? 'danger'
    : campaign.status === 'skipped' || campaign.status === 'paused'
      ? 'warning'
      : campaign.status === 'active'
        ? 'success'
        : campaign.status === 'queued'
          ? 'info'
          : 'neutral';

  return (
    <div className="rounded-2xl border border-border-default bg-surface-subtle px-3 py-2 text-sm">
      <Badge variant={variant}>{campaign.label}</Badge>
      <p className="mt-1 text-xs text-text-muted">{campaign.helper}</p>
      {campaign.skipReasons.length > 0 && (
        <p className="mt-1 text-xs font-medium text-text-secondary">Skipped: {campaign.skipReasons.join(', ')}</p>
      )}
    </div>
  );
}

function NextActionCard({ workflow, buttons, contactBlocked, hasActionableInterview, hasFailedMessage, onAction }) {
  if (!workflow?.nextAction) {
    return (
      <Card padding="normal" className="border-warning-border bg-warning-bg">
        <h2 className="text-lg font-bold text-warning-text">Next action</h2>
        <p className="mt-1 text-sm text-warning-text/80">Workflow status is unavailable. Refresh or try again.</p>
      </Card>
    );
  }

  const action = workflow.nextAction;
  const toneClasses = getWorkflowToneClasses(action.tone);

  return (
    <Card padding="normal" className={`${toneClasses.card}`} aria-live="polite">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] opacity-80">Next action</p>
      <h2 className="mt-2 text-xl font-bold">{action.title}</h2>
      <p className="mt-2 text-sm leading-6 opacity-90">{action.body}</p>

      <div className="mt-5 space-y-2">
        {buttons.length === 0 ? (
          <Button type="button" variant="secondary" disabled className="w-full justify-start">No action</Button>
        ) : buttons.map((button) => {
          const disabledForMissingTarget = (button.intent === 'mark_completed' || button.intent === 'mark_no_show') && !hasActionableInterview;
          const disabledForMissingFailedMessage = button.intent === 'retry_failed_text' && !hasFailedMessage;
          const disabled = button.disabled || disabledForMissingTarget || disabledForMissingFailedMessage;
          const disabledReason = button.disabledReason
            || (disabledForMissingTarget ? 'No scheduled interview is available to update' : null)
            || (disabledForMissingFailedMessage ? 'No failed message is available to retry' : null);
          const variant = button.source === 'primary' && !disabled ? primaryVariantForTone(action.tone) : 'secondary';
          return (
            <Button
              key={`${button.source}-${button.intent}-${button.label}`}
              type="button"
              variant={variant}
              disabled={disabled}
              title={disabledReason || undefined}
              onClick={() => onAction(button.intent)}
              className="w-full justify-start"
            >
              {button.label}
            </Button>
          );
        })}
      </div>

      {contactBlocked && (
        <p className="mt-3 text-xs font-medium opacity-80">Text actions are disabled because contact is blocked for this applicant.</p>
      )}
    </Card>
  );
}

function primaryVariantForTone(tone) {
  if (tone === 'red') return 'danger';
  if (tone === 'green') return 'success';
  return 'primary';
}

function InfoTile({ label, value, href }) {
  const content = (
    <>
      <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</span>
      <span className="mt-1 block truncate text-sm font-semibold text-text-primary">{value || '—'}</span>
    </>
  );

  if (href) {
    return (
      <a href={href} className="rounded-xl border border-border-default bg-white px-3 py-2 shadow-sm hover:border-info-border hover:bg-info-bg focus-ring">
        {content}
      </a>
    );
  }

  return <div className="rounded-xl border border-border-default bg-white px-3 py-2 shadow-sm">{content}</div>;
}

function PanelHeading({ eyebrow, title }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue">{eyebrow}</p>
      <h3 className="mt-1 text-base font-bold text-text-primary">{title}</h3>
    </div>
  );
}

function getSafeExternalUrl(value) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch (_) {
    return null;
  }
}

function getMessageStatusMeta(status) {
  switch (status) {
    case 'delivered':
      return { icon: '✓✓', label: 'Delivered', tone: 'normal' };
    case 'sent':
      return { icon: '✓', label: 'Sent', tone: 'normal' };
    case 'failed':
      return { icon: '✗', label: 'Failed to deliver', tone: 'failed' };
    case 'queued':
      return { icon: '⏳', label: 'Queued', tone: 'pending' };
    default:
      return { icon: '⏳', label: status || 'Pending', tone: 'pending' };
  }
}

function TimelineItem({ item, onRetry, isRetrying = false, retryDisabled = false, retryDisabledReason = null }) {
  if (item.type === 'message') {
    const isOutbound = item.direction === 'outbound';
    const statusMeta = getMessageStatusMeta(item.status);
    const sanitizedError = statusMeta.tone === 'failed' ? sanitizeProviderError(item.error_message) : '';

    return (
      <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-xs sm:max-w-sm rounded-2xl px-4 py-2.5 ${
          isOutbound
            ? statusMeta.tone === 'failed'
              ? 'bg-red-600 text-white rounded-br-sm'
              : 'bg-blue-600 text-white rounded-br-sm'
            : 'bg-gray-100 text-gray-900 rounded-bl-sm'
        }`}>
          <p className="text-sm whitespace-pre-wrap break-words">{item.body}</p>
          <div className={`flex items-center justify-between gap-2 mt-1 ${
            isOutbound
              ? statusMeta.tone === 'failed' ? 'text-red-100' : 'text-blue-100'
              : 'text-gray-400'
          }`}>
            <span className="text-xs">{formatRelative(item.created_at)}</span>
            {isOutbound && (
              <span className="text-xs font-medium" title={statusMeta.label}>
                {statusMeta.icon} {statusMeta.label}
              </span>
            )}
          </div>
          {isOutbound && statusMeta.tone === 'failed' && (
            <div className="mt-2 border-t border-red-400/60 pt-2 text-xs text-red-50">
              <p className="font-semibold">Delivery failed</p>
              {sanitizedError && (
                <p className="mt-0.5 break-words">Provider: {sanitizedError}</p>
              )}
              {onRetry && (
                <button
                  type="button"
                  onClick={() => onRetry(item.id)}
                  disabled={isRetrying || retryDisabled}
                  title={retryDisabledReason || undefined}
                  className="touch-target mt-2 rounded bg-white/95 px-2 py-1 font-medium text-red-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-70 focus-ring"
                >
                  {isRetrying ? 'Retrying...' : 'Retry send'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (item.type === 'stage_change') {
    return (
      <div className="flex justify-center">
        <div className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
          Stage: {item.from_stage ? `${STAGE_LABELS[item.from_stage]} → ` : ''}{STAGE_LABELS[item.to_stage]}
          <span className="ml-1.5">{formatRelative(item.created_at)}</span>
        </div>
      </div>
    );
  }

  if (item.type === 'interview') {
    return (
      <div className="flex justify-center">
        <div className={`text-xs px-3 py-1 rounded-full border ${
          item.status === 'scheduled' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
          item.status === 'completed' ? 'bg-green-50 text-green-700 border-green-100' :
          'bg-red-50 text-red-700 border-red-100'
        }`}>
          Interview {item.status}: {formatDateTime(item.scheduled_at)}
        </div>
      </div>
    );
  }

  return null;
}

function Toast({ message }) {
  return (
    <div className="fixed right-4 top-4 z-[60] rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800 shadow-lg" role="status">
      ✓ {message}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const panel = panelRef.current;
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = panel ? Array.from(panel.querySelectorAll(focusableSelector)).filter(el => !el.disabled) : [];
    const first = focusable[0];
    first?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || focusable.length === 0) return;
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 no-print animate-fade-in" role="presentation">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-xl bg-white shadow-xl animate-modal-in"
      >
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
          <h3 id="modal-title" className="font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title} dialog`}
            className="touch-target rounded-full text-gray-500 hover:text-gray-700 text-xl leading-none focus-ring"
          >
            ×
          </button>
        </div>
        <div className="p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

function getInitials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || '?';
}

